import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ChevronLeft, ChevronRight, List, Info, ArrowLeft } from 'lucide-react';
import { useCoursePlayer } from '../../modules/learning/hooks/useCoursePlayer';
import { useCourseProgressApi } from '../../modules/learning/hooks/useCourseProgressApi';
import { useCourseOutline } from '../../modules/learning/hooks/useCourseOutline';
import { CourseTocDrawer } from '../../modules/learning/components/CourseTocDrawer';
import { PageRenderer } from '../../modules/learning/components/PageRenderer';
import { getCourse } from '../../modules/learning/api/assetServiceClient';
import { showSuccessToastCenter } from '../../utils/errorHandler';

type LocationState = {
  recommendedCourseId?: string | null;
  recommendedPartLabel?: string;
  currentPartLabel?: string;
} | null;

export const StudentCoursePlayer: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as LocationState) ?? null;
  const { user } = useAuth();
  const studentId = user?.id ?? 'demo-student';

  const [tocOpen, setTocOpen] = useState(false);
  const [courseGrade, setCourseGrade] = useState<number | null>(null);

  const {
    selectedPageId,
    pageDetail,
    pageDetailLoading,
    pagesError,
    pageDetailError,
    selectLessonAndPage,
  } = useCoursePlayer(courseId);

  const { completedPageIds, lastPageId, markComplete, updateLastPageId } = useCourseProgressApi(
    courseId ?? undefined
  );

  const { units, flatItems, loading: outlineLoading } = useCourseOutline(
    courseId ?? undefined,
    completedPageIds,
    selectedPageId
  );

  const initRanForCourse = useRef<string | null>(null);

  // When outline is ready and no page selected yet, pick last visited or first page (unordered access)
  useEffect(() => {
    if (!courseId || flatItems.length === 0 || selectedPageId != null) return;
    if (initRanForCourse.current === courseId) return;
    initRanForCourse.current = courseId;

    const target = lastPageId
      ? flatItems.find((x) => x.pageId === lastPageId) ?? flatItems[0]
      : flatItems[0];
    selectLessonAndPage(target.lessonId, target.pageId);
  }, [courseId, flatItems, lastPageId, selectedPageId, selectLessonAndPage]);

  useEffect(() => {
    initRanForCourse.current = null;
  }, [courseId]);

  // Resolve grade for "Back to Parts" (from course detail or fallback 2)
  useEffect(() => {
    if (!courseId) {
      setCourseGrade(null);
      return;
    }
    getCourse(courseId).then((course) => {
      if (course?.grade != null) setCourseGrade(course.grade);
      else setCourseGrade(2);
    }).catch(() => setCourseGrade(2));
  }, [courseId]);

  useEffect(() => {
    if (pageDetail?.id && courseId && studentId) updateLastPageId(pageDetail.id);
  }, [pageDetail?.id, courseId, studentId, updateLastPageId]);

  const handlePageComplete = useCallback(() => {
    if (!selectedPageId) return;
    const willBeComplete =
      flatItems.length > 0 && completedPageIds.length + 1 >= flatItems.length;
    markComplete(selectedPageId);
    if (willBeComplete) showSuccessToastCenter('Part completed ✅');
  }, [selectedPageId, markComplete, flatItems.length, completedPageIds.length]);

  const handleTocSelect = useCallback(
    (pageId: string, lessonId: string) => {
      selectLessonAndPage(lessonId, pageId);
      setTocOpen(false);
    },
    [selectLessonAndPage]
  );

  const currentIndex = flatItems.findIndex((x) => x.pageId === selectedPageId);
  const nextItem = currentIndex >= 0 && currentIndex < flatItems.length - 1 ? flatItems[currentIndex + 1] : null;
  const prevItem = currentIndex > 0 ? flatItems[currentIndex - 1] : null;
  const hasNextPage = nextItem != null;
  const hasPrevPage = prevItem != null;
  const isAtLastPage = nextItem == null;

  const goNext = useCallback(() => {
    if (!nextItem) return;
    selectLessonAndPage(nextItem.lessonId, nextItem.pageId);
  }, [nextItem, selectLessonAndPage]);

  const goPrev = useCallback(() => {
    if (!prevItem) return;
    selectLessonAndPage(prevItem.lessonId, prevItem.pageId);
  }, [prevItem, selectLessonAndPage]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!pageDetail || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageDetail, goNext, goPrev]);

  return (
    <Layout>
      <PageHeader
        title="Course Player"
        subtitle={courseId ? 'View course content' : 'No course selected'}
        actions={
          courseId && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => navigate(`/student/grade/${courseGrade ?? 2}/parts`)}
                aria-label="Back to Parts"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Parts
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTocOpen(true)}
                aria-label="Open Page Titles"
              >
                <List className="w-4 h-4 mr-2" />
                Page Titles
              </Button>
            </div>
          )
        }
      />

      {!courseId && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center text-gray-600 dark:text-gray-400">
          Invalid course ID.
        </div>
      )}

      {courseId && (
        <div className="flex flex-col min-h-[calc(100vh-220px)]">
          {state?.recommendedCourseId && state.recommendedCourseId !== courseId && (
            <div className="mb-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-blue-800 dark:text-blue-200 flex items-center gap-2 text-sm">
              <Info className="w-4 h-4 shrink-0" />
              <span>
                You are starting {state.currentPartLabel ?? 'this part'}. Recommended: finish {state.recommendedPartLabel ?? 'Part-1'} first.
              </span>
            </div>
          )}
          <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <span className="font-semibold text-gray-900 dark:text-white">
                {pageDetail?.page_title ??
                  (pageDetail?.page_json_v1 as { page_title?: string } | undefined)?.page_title ??
                  'Content'}
              </span>
              {courseId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTocOpen(true)}
                  aria-label="Open Page Titles"
                  className="md:hidden"
                >
                  <List className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-hidden min-h-0 overflow-y-auto">
              {(outlineLoading ||
                (flatItems.length > 0 && selectedPageId == null) ||
                (selectedPageId != null && pageDetailLoading)) && (
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              )}
              {!outlineLoading &&
                flatItems.length > 0 &&
                selectedPageId != null &&
                !pageDetailLoading && (
                  <>
                    {pagesError && (
                      <p className="text-sm text-red-600 dark:text-red-400 p-4">{pagesError}</p>
                    )}
                    {pageDetailError && (
                      <p className="text-sm text-red-600 dark:text-red-400 p-4">{pageDetailError}</p>
                    )}
                    {!pagesError && !pageDetailError && (
                      <PageRenderer page={pageDetail} onComplete={handlePageComplete} />
                    )}
                  </>
                )}
              {!outlineLoading && flatItems.length === 0 && (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 p-6">
                  No pages in this course
                </div>
              )}
            </div>

            {pageDetail && flatItems.length > 0 && (
              <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goPrev}
                  disabled={!hasPrevPage}
                  aria-label="Previous page"
                  className="min-w-[100px]"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400 tabular-nums">
                  {currentIndex >= 0 ? (
                    <>Page {currentIndex + 1} of {flatItems.length}</>
                  ) : (
                    '—'
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePageComplete}
                    disabled={selectedPageId != null && completedPageIds.includes(selectedPageId)}
                    aria-label="Mark page complete"
                  >
                    {selectedPageId != null && completedPageIds.includes(selectedPageId)
                      ? 'Completed'
                      : 'Mark Complete'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={goNext}
                    disabled={!hasNextPage}
                    aria-label="Next page"
                    className="min-w-[100px]"
                  >
                    {isAtLastPage ? (
                      'Completed'
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <CourseTocDrawer
        open={tocOpen}
        onClose={() => setTocOpen(false)}
        units={units}
        onSelect={handleTocSelect}
      />
    </Layout>
  );
};
